import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link
} from '@mui/material';
import { LocationCity, ArrowForward, ArrowBack, NavigateNext } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
  user_roles: string[];
}

interface SiteSelectionProps {
  assetOwnerId: number;
  assetOwnerName: string;
  onSiteSelect: (siteId: number, siteName: string) => void;
  onBack: () => void;
}

const SiteSelection: React.FC<SiteSelectionProps> = ({ 
  assetOwnerId, 
  assetOwnerName, 
  onSiteSelect, 
  onBack 
}) => {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSites();
  }, [assetOwnerId, user]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get sites for this asset owner from user's site roles
      if (!user?.site_roles) {
        setSites([]);
        return;
      }

      // Group sites by site_id to deduplicate and collect all roles
      const siteMap = new Map();
      user.site_roles
        .filter(role => role.asset_owner_id === assetOwnerId && role.is_active)
        .forEach(role => {
          const key = role.site_id;
          if (!siteMap.has(key)) {
            siteMap.set(key, {
              id: role.site_id,
              name: role.site_name,
              code: role.site_code,
              site_type: 'Power Station', // Default type, could be fetched from API
              user_roles: []
            });
          }
          siteMap.get(key).user_roles.push(role.role);
        });

      setSites(Array.from(siteMap.values()));
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          Loading sites...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (sites.length === 0) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          No sites found for this asset owner. Please contact your administrator.
        </Alert>
        <Button onClick={onBack} startIcon={<ArrowBack />}>
          Back to Asset Owners
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => { e.preventDefault(); onBack(); }}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Asset Owners
        </Link>
        <Typography color="text.primary">{assetOwnerName}</Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button 
        onClick={onBack} 
        startIcon={<ArrowBack />} 
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back to Asset Owners
      </Button>

      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Select Site
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Choose a site within {assetOwnerName} to access operations
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {sites.map((site) => (
          <Grid item xs={12} sm={6} md={4} key={site.id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => onSiteSelect(site.id, site.name)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      margin: '0 auto 16px',
                      bgcolor: 'secondary.main',
                      fontSize: '1.5rem'
                    }}
                  >
                    <LocationCity sx={{ fontSize: 40 }} />
                  </Avatar>
                  
                  <Typography variant="h5" component="h2" gutterBottom>
                    {site.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {site.code} • {site.site_type}
                  </Typography>
                  
                  <Box display="flex" justifyContent="center" gap={1} mb={2} flexWrap="wrap">
                    {site.user_roles.map((role, index) => (
                      <Chip 
                        key={index}
                        label={role} 
                        color="primary" 
                        size="small" 
                      />
                    ))}
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
                    <Typography variant="body2" color="secondary">
                      Access Site Dashboard
                    </Typography>
                    <ArrowForward color="secondary" sx={{ ml: 1 }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SiteSelection; 